import React, { useState } from 'react';
import Input from '../../UI/Input';
import Button from '../../UI/Button';
import AISuggestionModal from '../AISuggestionModal';

export default function ProjectsForm({ projects = [], onChange }) {
	const items = projects || [];
	const [aiIdx, setAiIdx] = useState(null);

	function add() {
		onChange([...(items || []), { title: '', description: '', technologies: [], link: '', github: '', startDate: '', endDate: '', ongoing: false, achievements: [] }]);
	}
	function remove(i) {
		onChange(items.filter((_, idx) => idx !== i));
	}
	function move(i, dir) {
		const j = i + dir;
		if (j < 0 || j >= items.length) return;
		const next = [...items];
		[next[i], next[j]] = [next[j], next[i]];
		onChange(next);
	}
	function update(i, key, value) {
		const next = [...items];
		next[i] = { ...next[i], [key]: value };
		if (key === 'ongoing' && value) next[i].endDate = '';
		onChange(next);
	}
	function addTech(i, text) {
		const next = [...items];
		next[i].technologies = [...(next[i].technologies || []), text];
		onChange(next);
	}
	function removeTech(i, idx) {
		const next = [...items];
		next[i].technologies = (next[i].technologies || []).filter((_, k) => k !== idx);
		onChange(next);
	}
	function addAchievement(i, text) {
		const next = [...items];
		next[i].achievements = [...(next[i].achievements || []), text];
		onChange(next);
	}
	function removeAchievement(i, idx) {
		const next = [...items];
		next[i].achievements = (next[i].achievements || []).filter((_, k) => k !== idx);
		onChange(next);
	}

	return (
		<div className="space-y-4">
			{!items.length && <div className="text-sm text-gray-500">No projects yet.</div>}
			{items.map((p, i) => (
				<div key={i} className="border rounded-md p-4 space-y-3">
					<div className="flex items-center justify-between">
						<div className="text-sm text-gray-500">{p.title}</div>
						<div className="flex items-center gap-2">
							<Button variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}>
								Up
							</Button>
							<Button variant="ghost" onClick={() => move(i, 1)} disabled={i === items.length - 1}>
								Down
							</Button>
							<Button variant="ghost" onClick={() => remove(i)}>
								Remove
							</Button>
						</div>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						<Input name="title" label="Project Name" value={p.title || ''} onChange={(e) => update(i, 'title', e.target.value)} required />
						<Input name="link" label="Project Link / URL" value={p.link || ''} onChange={(e) => update(i, 'link', e.target.value)} />
						<Input name="github" label="GitHub Link" value={p.github || ''} onChange={(e) => update(i, 'github', e.target.value)} />
						<div className="grid grid-cols-2 gap-3">
							<Input name="startDate" label="Start (YYYY-MM)" value={p.startDate || ''} onChange={(e) => update(i, 'startDate', e.target.value)} />
							<Input name="endDate" label="End (YYYY-MM)" value={p.endDate || ''} onChange={(e) => update(i, 'endDate', e.target.value)} disabled={p.ongoing} />
						</div>
						<label className="flex items-center gap-2 text-sm">
							<input type="checkbox" checked={!!p.ongoing} onChange={(e) => update(i, 'ongoing', e.target.checked)} />
							Ongoing
						</label>
						<div className="md:col-span-2">
							<label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
							<textarea
								className="w-full rounded-md border border-gray-300 px-3 py-2"
								rows="3"
								value={p.description || ''}
								onChange={(e) => update(i, 'description', e.target.value)}
							/>
							<div className="mt-2">
								<Button variant="secondary" type="button" onClick={() => setAiIdx(i)}>
									AI Suggest Description
								</Button>
							</div>
						</div>
						<div className="md:col-span-2">
							<label className="block text-sm font-medium text-gray-700 mb-1">Technologies Used</label>
							<TechChips items={p.technologies || []} onAdd={(t) => addTech(i, t)} onRemove={(k) => removeTech(i, k)} />
						</div>
						<div className="md:col-span-2">
							<label className="block text-sm font-medium text-gray-700 mb-1">Key Achievements</label>
							<Bullets items={p.achievements || []} onAdd={(t) => addAchievement(i, t)} onRemove={(k) => removeAchievement(i, k)} />
						</div>
					</div>
				</div>
			))}
			<Button variant="ghost" onClick={add}>
				Add Project
			</Button>
			<AISuggestionModal
				isOpen={aiIdx !== null}
				onClose={() => setAiIdx(null)}
				sectionType="project"
				promptContext={{
					position: items[aiIdx || 0]?.title || 'Project',
					currentDescription: items[aiIdx || 0]?.description || ''
				}}
				onSelect={(text) => {
					const next = [...items];
					next[aiIdx] = { ...next[aiIdx], description: text };
					onChange(next);
				}}
			/>
		</div>
	);
}

function TechChips({ items, onAdd, onRemove }) {
	const [val, setVal] = React.useState('');
	function onKeyDown(e) {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (!val.trim()) return;
			onAdd(val.trim());
			setVal('');
		}
	}
	return (
		<div>
			<div className="flex flex-wrap gap-2">
				{(items || []).map((s, i) => (
					<span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
						{s}{' '}
						<button type="button" className="ml-1 text-red-600" onClick={() => onRemove(i)}>
							×
						</button>
					</span>
				))}
			</div>
			<input
				className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
				placeholder="Type and press Enter to add"
				value={val}
				onChange={(e) => setVal(e.target.value)}
				onKeyDown={onKeyDown}
			/>
		</div>
	);
}

function Bullets({ items, onAdd, onRemove }) {
	const [val, setVal] = React.useState('');
	function submit(e) {
		e.preventDefault();
		if (!val.trim()) return;
		onAdd(val.trim());
		setVal('');
	}
	return (
		<div>
			<ul className="list-disc ml-5 space-y-1">
				{items.map((b, i) => (
					<li key={i} className="flex items-center gap-2">
						<span className="flex-1">{b}</span>
						<button className="text-sm text-red-600" type="button" onClick={() => onRemove(i)}>
							Remove
						</button>
					</li>
				))}
			</ul>
			<form className="mt-2 flex gap-2" onSubmit={submit}>
				<input
					className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
					placeholder="Add achievement and press Enter"
					value={val}
					onChange={(e) => setVal(e.target.value)}
				/>
				<Button type="submit" variant="ghost">
					Add
				</Button>
			</form>
		</div>
	);
}


